import * as React from "react";

const MOBILE_BREAKPOINT = 1024;

/** Detect iPad via user agent — covers both Safari and in-app webviews */
function detectIPad(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  // Classic iPad UA string
  if (/iPad/i.test(ua)) return true;
  // Modern iPadOS 13+ reports as Macintosh; distinguish by touch support
  if (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1) return true;
  return false;
}

/** Cached at module level — UA doesn't change within a session */
let _isIPad: boolean | null = null;
export function isIPad(): boolean {
  if (_isIPad === null) _isIPad = detectIPad();
  return _isIPad;
}

/**
 * Detects mobile devices regardless of orientation.
 * iPads are always treated as mobile to ensure hamburger menu shows.
 * Uses the smaller screen dimension so landscape doesn't flip to "desktop".
 */
export function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState<boolean | undefined>(undefined);

  React.useEffect(() => {
    const check = () => {
      // iPads always get mobile UI regardless of screen width
      if (isIPad()) {
        setIsMobile(true);
        return;
      }
      const smallerDimension = Math.min(window.innerWidth, window.innerHeight);
      const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      setIsMobile(smallerDimension < MOBILE_BREAKPOINT && hasTouch);
    };
    check();
    window.addEventListener("resize", check);
    window.addEventListener("orientationchange", check);
    return () => {
      window.removeEventListener("resize", check);
      window.removeEventListener("orientationchange", check);
    };
  }, []);

  return !!isMobile;
}
